import { createContext, useContext, useMemo, useReducer } from "react";

import type { Dispatch, ReactNode, JSX } from "react";
import type {
    ActionControl,
    AppObject,
    SchoolYear,
    DistrictWindow,
    LocationDto,
    StudentImage,
    StudentInfo,
    Student,
    TimePeriod,
    StudentOe
} from "@/types/GlobalState";

import type { UserDetails } from "@/types";
// ─── State Shape ─────────────────────────────────────────────────────────────

export interface GlobalState {
    allLocationDtos?: LocationDto[];
    guardianState?: boolean;
    gradingProfiles?: unknown;
    lang?: string;
    locKey?: null | number | string;
    oeActionControls?: ActionControl[];
    oeActiveDistrictWindow?: DistrictWindow;
    oeDistrictWindows?: DistrictWindow[];
    oeSchoolYearRange?: SchoolYear;
    oeStudentEligibility?: unknown;
    oeSchoolYearKey?: string;
    saeActionControls?: ActionControl[];
    schoolYearDto?: SchoolYear;
    schoolYearKey?: string;
    studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
    studentImageList?: StudentImage[];
    studentInfoDto?: StudentInfo;
    studentsCharterSchool?: unknown;
    studentsFlag?: { IC?: boolean; OE?: boolean; [key: string]: boolean | undefined };
    studentsHomeSchooled?: unknown;
    studentsIc?: Student[];
    studentsOe?: StudentOe[];
    thruDashboard?: boolean;
    timePeriods?: TimePeriod[];
    token: string | null;
    userDetails?: UserDetails;
    userDetailsError?: boolean;
    username: string | null;
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export type GlobalAction =
    | { type: "AllLocationDtos"; allLocationDtos: LocationDto[] }
    | { type: "GuardianState"; guardianState: boolean }
    | { type: "GradingProfiles"; gradingProfiles: unknown }
    | { type: "Lang"; lang: string }
    | { type: "LocKey"; locKey: string }
    | { type: "OeActionControls"; oeActionControls: ActionControl[] }
    | { type: "OeActiveDistrictWindow"; oeActiveDistrictWindow: DistrictWindow }
    | { type: "OeDistrictWindows"; oeDistrictWindows: DistrictWindow[] }
    | { type: "OeSchoolYearRange"; oeSchoolYearRange: SchoolYear }
    | { type: "OeStudentEligibility"; oeStudentEligibility: unknown }
    | { type: "OeSchoolYearKey"; oeSchoolYearKey: string }
    | { type: "SaeActionControls"; saeActionControls: ActionControl[] }
    | { type: "SchoolYearDto"; schoolYearDto: SchoolYear }
    | { type: "SchoolYearKey"; schoolYearKey: string }
    | { type: "StudentAppObjects"; studentAppObjects: Record<string, Record<string, { appObj: AppObject | null }>> }
    | { type: "StudentImageList"; studentImageList: StudentImage[] }
    | { type: "StudentInfoDto"; studentInfoDto: StudentInfo }
    | { type: "StudentsCharterSchool"; studentsCharterSchool: unknown }
    | { type: "StudentsFlag"; studentsFlag: { IC?: boolean; OE?: boolean; [key: string]: boolean | undefined } }
    | { type: "StudentsHomeSchooled"; studentsHomeSchooled: unknown }
    | { type: "StudentsIc"; studentsIc: Student[] }
    | { type: "StudentsOe"; studentsOe: StudentOe[] }
    | { type: "ThruDashboard"; thruDashboard: boolean }
    | { type: "TimePeriods"; timePeriods: TimePeriod[] }
    | { type: "Token"; token: string | null }
    | { type: "UserDetails"; userDetails: UserDetails }
    | { type: "UserDetailsError"; userDetailsError: boolean }
    | { type: "Username"; username: string | null };

// ─── Reducer ─────────────────────────────────────────────────────────────────

const initialState: GlobalState = { token: null, username: null };

function reducer(state: GlobalState, action: GlobalAction): GlobalState {
    switch (action.type) {
        case "AllLocationDtos":
            return { ...state, allLocationDtos: action.allLocationDtos };
        case "GuardianState":
            return { ...state, guardianState: action.guardianState };
        case "GradingProfiles":
            return { ...state, gradingProfiles: action.gradingProfiles };
        case "Lang":
            return { ...state, lang: action.lang };
        case "LocKey":
            return { ...state, locKey: action.locKey };
        case "OeActionControls":
            return { ...state, oeActionControls: action.oeActionControls };
        case "OeActiveDistrictWindow":
            return { ...state, oeActiveDistrictWindow: action.oeActiveDistrictWindow };
        case "OeDistrictWindows":
            return { ...state, oeDistrictWindows: action.oeDistrictWindows };
        case "OeSchoolYearRange":
            return { ...state, oeSchoolYearRange: action.oeSchoolYearRange };
        case "OeStudentEligibility":
            return { ...state, oeStudentEligibility: action.oeStudentEligibility };
        case "OeSchoolYearKey":
            return { ...state, oeSchoolYearKey: action.oeSchoolYearKey };
        case "SaeActionControls":
            return { ...state, saeActionControls: action.saeActionControls };
        case "SchoolYearDto":
            return { ...state, schoolYearDto: action.schoolYearDto };
        case "SchoolYearKey":
            return { ...state, schoolYearKey: action.schoolYearKey };
        case "StudentAppObjects": {
            const nextStudentAppObjects = { ...state.studentAppObjects };
            if (action.studentAppObjects) {
                for (const studentKey of Object.keys(action.studentAppObjects)) {
                    nextStudentAppObjects[studentKey] = {
                        ...nextStudentAppObjects[studentKey],
                        ...action.studentAppObjects[studentKey]
                    };
                }
            }
            return { ...state, studentAppObjects: nextStudentAppObjects };
        }
        case "StudentImageList":
            return { ...state, studentImageList: action.studentImageList };
        case "StudentInfoDto":
            return { ...state, studentInfoDto: action.studentInfoDto };
        case "StudentsCharterSchool":
            return { ...state, studentsCharterSchool: action.studentsCharterSchool };
        case "StudentsFlag":
            return { ...state, studentsFlag: { ...state.studentsFlag, ...action.studentsFlag } };
        case "StudentsHomeSchooled":
            return { ...state, studentsHomeSchooled: action.studentsHomeSchooled };
        case "StudentsIc":
            return { ...state, studentsIc: action.studentsIc };
        case "StudentsOe":
            return { ...state, studentsOe: action.studentsOe };
        case "ThruDashboard":
            return { ...state, thruDashboard: action.thruDashboard };
        case "TimePeriods":
            return { ...state, timePeriods: action.timePeriods };
        case "Token":
            return { ...state, token: action.token };
        case "UserDetails":
            return { ...state, userDetails: action.userDetails, userDetailsError: false };
        case "UserDetailsError":
            return { ...state, userDetailsError: action.userDetailsError };
        case "Username":
            return { ...state, username: action.username };
        default:
            return state;
    }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface GlobalContextType {
    state: GlobalState;
    dispatch: Dispatch<GlobalAction>;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ContextProviderProps {
    children?: ReactNode;
    initialState?: Partial<GlobalState>;
}

const ContextProvider = ({ children, initialState: initialStateProp }: ContextProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(reducer, { ...initialState, ...initialStateProp });

    const contextValue = useMemo((): GlobalContextType => ({ state, dispatch }), [state]);

    return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
};

// ─── Custom Hook ──────────────────────────────────────────────────────────────

/**
 * Custom hook for consuming Global Context.
 * Usage: const { state, dispatch } = useGlobalContext();
 * Throws if used outside of <ContextProvider>.
 */
const useGlobalContext = (): GlobalContextType => {
    const context = useContext(GlobalContext);
    if (context === undefined) {
        throw new Error("useGlobalContext must be used within a ContextProvider");
    }
    return context;
};

// Keep GlobalContext export for any legacy direct-access (private route, etc.)
export { ContextProvider, GlobalContext, useGlobalContext };
